"use client";

import {
  ComponentCmsDataFormQuestion,
  Enum_Componentcmsdataformquestion_Desktop_Layout,
  Enum_Componentcmsdataformquestion_Mobile_Layout,
} from "@/generated";
import clsx from "clsx";
import { useViewport } from "@/hooks/useViewport";

interface BackgroundMediaProps {
  question: ComponentCmsDataFormQuestion;
  displayType: "desktop" | "mobile";
  layout?:
    | Enum_Componentcmsdataformquestion_Desktop_Layout
    | Enum_Componentcmsdataformquestion_Mobile_Layout;
}

export const BackgroundMedia: React.FC<BackgroundMediaProps> = ({
  question,
  displayType,
  layout,
}) => {
  const { isMobile, isLoading } = useViewport();
  const hasVideo =
    question.video?.data !== null && question.video?.data !== undefined;
  const hasImage =
    question.image?.data !== null && question.image?.data !== undefined;

  // Show nothing while loading to prevent flash of content
  if (isLoading) return null;

  // Only render the appropriate layout based on viewport
  if (isMobile && displayType === "desktop") return null;
  if (!isMobile && displayType === "mobile") return null;

  if (!hasImage && !hasVideo) return null;

  switch (displayType) {
    case "desktop":
      switch (layout) {
        case Enum_Componentcmsdataformquestion_Desktop_Layout.FullBackground:
          return (
            <div className="absolute inset-0 z-0">
              {hasVideo ? (
                <video
                  className="w-full h-full object-cover"
                  loop
                  autoPlay
                  playsInline
                  preload="auto"
                >
                  <source
                    src={question.video?.data?.attributes?.url}
                    type="video/mp4"
                  />
                  Browserul tău nu suportă elementul video.
                </video>
              ) : (
                hasImage && (
                  <img
                    src={question.image?.data?.attributes?.url}
                    alt={
                      question.image?.data?.attributes?.alternativeText ||
                      `Imagine întrebare "${question.title}"`
                    }
                    className="w-full h-full object-cover"
                  />
                )
              )}
            </div>
          );
        case Enum_Componentcmsdataformquestion_Desktop_Layout.LandscapeImageLeft:
        case Enum_Componentcmsdataformquestion_Desktop_Layout.LandscapeImageRight:
        case Enum_Componentcmsdataformquestion_Desktop_Layout.StartScreen:
          return (
            <div className="flex-1">
              {hasVideo ? (
                <video
                  className="w-auto h-auto max-w-[80%] max-h-[80vh] rounded-lg shadow-lg object-contain"
                  loop
                  autoPlay
                  playsInline
                >
                  <source
                    src={question.video?.data?.attributes?.url}
                    type="video/mp4"
                  />
                  Browserul tău nu suportă elementul video.
                </video>
              ) : (
                hasImage && (
                  <img
                    className="w-auto h-auto max-w-[80%] max-h-[80vh] rounded-lg shadow-lg object-contain"
                    src={question.image?.data?.attributes?.url}
                    alt="Imagine întrebare"
                  />
                )
              )}
            </div>
          );
        case Enum_Componentcmsdataformquestion_Desktop_Layout.LandscapeImageMiddle:
          return (
            <div
              className={clsx([
                "w-full max-w-md",
                question.center_text ? "mx-auto" : "ml-0",
              ])}
            >
              {hasVideo ? (
                <video
                  className="w-full h-auto rounded-lg shadow-lg"
                  loop
                  autoPlay
                  playsInline
                >
                  <source
                    src={question.video?.data?.attributes?.url}
                    type="video/mp4"
                  />
                  Browserul tău nu suportă elementul video.
                </video>
              ) : (
                hasImage && (
                  <img
                    className="w-full h-auto rounded-lg shadow-lg"
                    src={question.image?.data?.attributes?.url}
                    alt="Imagine întrebare"
                  />
                )
              )}
            </div>
          );
        case Enum_Componentcmsdataformquestion_Desktop_Layout.FullImageLeft:
        case Enum_Componentcmsdataformquestion_Desktop_Layout.FullImageRight:
          return (
            <div className="h-full w-auto flex-shrink-0">
              {hasVideo ? (
                <video
                  className="w-full h-full object-cover shadow-lg"
                  loop
                  autoPlay
                  playsInline
                >
                  <source
                    src={question.video?.data?.attributes?.url}
                    type="video/mp4"
                  />
                  Browserul tău nu suportă elementul video.
                </video>
              ) : (
                hasImage && (
                  <img
                    src={question.image?.data?.attributes?.url}
                    alt="Imagine întrebare"
                    className="w-full h-full object-cover shadow-lg"
                  />
                )
              )}
            </div>
          );
      }
      break;
    case "mobile":
      switch (layout) {
        case Enum_Componentcmsdataformquestion_Mobile_Layout.FullBackground:
          return (
            <div className="absolute inset-0 z-0">
              {hasVideo ? (
                <video
                  className="w-full h-full object-cover"
                  loop
                  autoPlay
                  playsInline
                >
                  <source
                    src={question.video?.data?.attributes?.url}
                    type="video/mp4"
                  />
                  Browserul tău nu suportă elementul video.
                </video>
              ) : (
                hasImage && (
                  <img
                    src={question.image?.data?.attributes?.url}
                    alt="Background"
                    className="w-full h-full object-cover"
                  />
                )
              )}
            </div>
          );
        case Enum_Componentcmsdataformquestion_Mobile_Layout.LandscapeImageTop:
        case Enum_Componentcmsdataformquestion_Mobile_Layout.LandscapeImageMiddle:
        case Enum_Componentcmsdataformquestion_Mobile_Layout.StartScreen:
          return (
            <div
              className={clsx([
                "w-full max-w-md",
                question.center_text ? "mx-auto" : "ml-0",
              ])}
            >
              {hasVideo ? (
                <video
                  className="w-full h-auto rounded-lg shadow-lg"
                  loop
                  autoPlay
                  playsInline
                >
                  <source
                    src={question.video?.data?.attributes?.url}
                    type="video/mp4"
                  />
                  Browserul tău nu suportă elementul video.
                </video>
              ) : (
                hasImage && (
                  <img
                    src={question.image?.data?.attributes?.url}
                    alt="Imagine întrebare"
                    className="w-full h-auto rounded-lg shadow-lg"
                  />
                )
              )}
            </div>
          );
        case Enum_Componentcmsdataformquestion_Mobile_Layout.FullImageTop:
          return (
            <div className="w-full flex-shrink-0">
              {hasVideo ? (
                <video className="w-full h-auto" loop autoPlay playsInline>
                  <source
                    src={question.video?.data?.attributes?.url}
                    type="video/mp4"
                  />
                  Browserul tău nu suportă elementul video.
                </video>
              ) : (
                hasImage && (
                  <img
                    src={question.image?.data?.attributes?.url}
                    alt="Imagine întrebare"
                    className="w-full h-auto"
                  />
                )
              )}
            </div>
          );
      }
      break;
  }
};
